import * as React from "react";
import { gql, useMutation, useApolloClient } from "@apollo/client";
import { 
  LoginAdminMutationMutation, 
  LoginAdminMutationMutationVariables
} from "../../../../schemaTypes";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";

// Define the possible return type for submit
type SubmitResult =
  | NormalizedErrorMap
  | { sessionId: string | null | undefined; sessionRole: string | null | undefined };

const loginAdminMutation = gql`
  mutation LoginAdminMutation(
    $adminId: String!
    $password: String!
  ) {
    loginAdmin(
      adminId: $adminId
      password: $password
    ) {
      errors {
        path
        message
      }
      sessionId
      sessionRole
    }
  }
`;

interface Props {
  onSessionId?: (sessionId: string, sessionRole?: string) => void;
  children: (
    data: {
      submit: (
        values: LoginAdminMutationMutationVariables
      ) => Promise<SubmitResult>;
    }
  ) => React.ReactElement | null;
}

export const LoginAdminController: React.FC<Props> = ({ children, onSessionId }) => {
  const [_loginAdminMutation] = useMutation<LoginAdminMutationMutation, LoginAdminMutationMutationVariables>(loginAdminMutation);
  const client = useApolloClient();

  const submit = async (values: LoginAdminMutationMutationVariables): Promise<SubmitResult> => {
    const { data } = await _loginAdminMutation({ variables: values });

    if (data?.loginAdmin.errors) {
      return normalizeErrors(data.loginAdmin.errors);
    }

    if (data?.loginAdmin.sessionId && onSessionId) {
      onSessionId(data.loginAdmin.sessionId, data.loginAdmin.sessionRole ?? undefined);
    }

    await client.resetStore();

    return {
      sessionId: data?.loginAdmin.sessionId,
      sessionRole: data?.loginAdmin.sessionRole
    };
  };

  return children({ submit });
};